import { ClockClockwiseIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetSettingsByToken } from '@/api/market-shop/client/get-settings-by-token'
import { useGetClientOrders } from '@/api/market-shop/orders/get-client-orders'
import { GetMarketShopCategoriesResponse } from '@/api/market-shop/products/get-market-shop-categories'
import { Button } from '@/components/business/market-shop/button'
import { Chip } from '@/components/business/market-shop/chip'
import { OrderStatusBar } from '@/components/business/market-shop/order-status-bar'
import { OrderStatusBarStatus } from '@/components/business/market-shop/order-status-bar/order-status-bar.types'
import { Icon } from '@/components/ui/icon'
import { TextInput } from '@/components/ui/text-input'
import { popupFactory } from '@/factories'
import { useFilters } from '@/hooks'
import { MarketShopClientOrderModel } from '@/models'
import {
  getOrderStatusToTextsMap,
  marketShopProductsInitialFilters,
} from '@/pages/market-shop/market-shop.constants'
import { OrderDetails } from '@/pages/market-shop/order-details'
import { OrdersHistory } from '@/pages/market-shop/orders-history'

import { marketShopOrdersInitialFilters, SUPPORTED_STATUSES } from './header.constants'
import * as S from './header.styled'
import { WorkingHours } from './working-hours'

export type HeaderProps = {
  categories: GetMarketShopCategoriesResponse
  useFiltersOutput: ReturnType<typeof useFilters<typeof marketShopProductsInitialFilters>>
}

export const Header = ({ categories, useFiltersOutput }: HeaderProps) => {
  const { t } = useTranslation(['market-shop'])
  const categoryIdFilter = useFiltersOutput.getFilter('categoryId')

  const isPointerDownRef = useRef(false)
  const startXRef = useRef(0)
  const startScrollXRef = useRef(0)

  const { isResidentFlow } = useGetSettingsByToken()

  const { data: clientOrders } = useGetClientOrders({
    data: marketShopOrdersInitialFilters,
    enabled: isResidentFlow,
  })
  const filteredOrders = useMemo(() => {
    const pageOne = clientOrders?.pages[0]?.data
    if (!pageOne) return []
    return pageOne.filter(
      (order): order is MarketShopClientOrderModel & { status: OrderStatusBarStatus } =>
        SUPPORTED_STATUSES.includes(order.status),
    )
  }, [clientOrders])
  const hasOrders = !!filteredOrders.length

  const handleOrderClick = (order: MarketShopClientOrderModel) => {
    popupFactory.open(OrderDetails, { order })
  }

  const handleOrdersHistoryClick = () => {
    popupFactory.open(OrdersHistory, {})
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    useFiltersOutput.setFilter('search', e.target.value)
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    isPointerDownRef.current = true
    startXRef.current = e.clientX
    startScrollXRef.current = e.currentTarget.scrollLeft
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPointerDownRef.current) return
    const dx = e.clientX - startXRef.current
    // NOTE: do nothing if dx less than 1 px
    if (Math.abs(dx) < 1) return
    e.currentTarget.scrollLeft = startScrollXRef.current - dx
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerUpOrCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    isPointerDownRef.current = false
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  return (
    <S.Header>
      <WorkingHours />
      {hasOrders && (
        <S.Orders
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUpOrCancel}
          onPointerCancel={handlePointerUpOrCancel}
        >
          {filteredOrders.map((order) => {
            const { status } = order
            const { title, message } = getOrderStatusToTextsMap(t)[status](order)
            return (
              <OrderStatusBar
                key={order.id}
                title={title}
                message={message}
                status={status}
                onTrackClick={() => handleOrderClick(order)}
              />
            )
          })}
        </S.Orders>
      )}
      <S.MainBar>
        <TextInput
          id="search-input"
          icon={MagnifyingGlassIcon}
          placeholder={t('market-shop:header.searchPlaceholder')}
          value={useFiltersOutput.getFilter('search')}
          onChange={handleSearchChange}
        />
        {isResidentFlow && (
          <Button onlyBox use="secondary-gray" onClick={handleOrdersHistoryClick}>
            <Icon icon={ClockClockwiseIcon} />
          </Button>
        )}
      </S.MainBar>
      <S.Categories
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUpOrCancel}
        onPointerCancel={handlePointerUpOrCancel}
      >
        <Chip
          variant={categoryIdFilter === undefined ? 'active' : 'default'}
          onClick={() => {
            useFiltersOutput.setFilter('categoryId', undefined)
          }}
        >
          {t('market-shop:header.allCategory')}
        </Chip>
        {categories.map((category) => {
          const isActive = !!categoryIdFilter?.includes(category.id)
          return (
            <Chip
              key={category.id}
              variant={isActive ? 'active' : 'default'}
              onClick={() => {
                if (isActive) return
                useFiltersOutput.setFilter('categoryId', [category.id])
              }}
            >
              {category.name}
            </Chip>
          )
        })}
      </S.Categories>
    </S.Header>
  )
}
